import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FiChevronDown } from 'react-icons/fi';

/**
 * A field-shaped trigger (label, value, hint) that opens a panel under it.
 * Closes on an outside click and on Escape. Used for trip length and
 * travellers in the search form.
 */
function PackageDropdown({
  icon,
  label,
  value,
  hint,
  hasError,
  align,
  children,
  footer,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target))
        setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="pkg-dropdown" ref={rootRef}>
      <button
        type="button"
        className={`pkg-field ${open ? 'pkg-field--open' : ''} ${
          hasError ? 'pkg-field--error' : ''
        }`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="pkg-field__label">
          {icon}
          {label}
        </span>
        <span className="pkg-field__value">{value}</span>
        {hint && <span className="pkg-field__hint">{hint}</span>}
        <FiChevronDown className="pkg-field__caret" aria-hidden />
      </button>
      {open && (
        <div
          className={`pkg-dropdown__panel pkg-dropdown__panel--${align}`}
          role="dialog"
          aria-label={label}
        >
          {children}
          <div className="pkg-dropdown__foot">
            {footer}
            <button
              type="button"
              className="pkg-dropdown__done"
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

PackageDropdown.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  hint: PropTypes.string,
  hasError: PropTypes.bool,
  align: PropTypes.oneOf(['left', 'right']),
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
};

PackageDropdown.defaultProps = {
  icon: null,
  hint: '',
  hasError: false,
  align: 'left',
  footer: null,
};

export default PackageDropdown;
